/**
 * Utility functions for request-related operations
 * Shared across multiple components to avoid duplication
 */

/**
 * Formats status text for display
 * @param {string} status - The status to format
 * @returns {string} Formatted status text
 */
export const formatStatus = (status) => {
    if (!status) return 'Unknown';

    switch (status) {
        case 'PENDING_SUB_APPROVER':
            return 'Pending Sub-Approver';
        case 'PENDING_APPROVER':
            return 'Pending Approver';
        case 'PENDING_SUB_APPROVER':
            return 'Pending Sub-Approver';
        case 'PENDING_DEVOPS_APPROVAL':
            return 'Pending DevOps Approval';
        case 'DEPROVISIONED_MANUALLY':
            return 'Decommissioning';
        case 'IN_PROGRESS':
            return 'In Progress';
        case 'FAILED':
            return 'Failed';
        case 'WITHDRAWN':
            return 'Withdrawn';
        case 'TENANT IS READY':
            return 'Tenant is Ready';
        case 'MIGRATION IS IN PROGRESS':
            return 'Migration is in Progress';
        case 'MULTI TENANCY FORM COMPLETED':
            return 'Multi Tenancy Form Completed';
        case 'FAILED-VM Creation':
            return 'Failed - VM Creation';
        case 'FAILED-Setup':
            return 'Failed - Setup';
        case 'FAILED-Multi Tenancy Form Script':
            return 'Failed - Multi Tenancy Form Script';
        case 'FAILED-Multi Tenancy Form Processing':
            return 'Failed - Multi Tenancy Form Processing';
        case 'VM_CREATION_CANCELLED':
            return 'VM Creation Cancelled';
        case 'LINODE_INSTANCE_CREATION_CANCELLED':
            return 'Linode Instance Creation Cancelled';
        default:
            return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_/g, ' ');
    }
};

/**
 * Gets the semantic UI color class for a status
 * @param {string} status - The status to get color for
 * @returns {string} Semantic UI color class
 */
export const getStatusColor = (status) => {
    if (!status) return '';

    const normalizedStatus = status.toLowerCase().replace(/-/g, ' ');

    if (normalizedStatus.includes('failed')) {
        return 'error';
    } else if (normalizedStatus.includes('ready') || normalizedStatus.includes('created')) {
        return 'positive';
    } else if (
        normalizedStatus.includes('pending') ||
        normalizedStatus.includes('deprovisioned manually') ||
        normalizedStatus === 'deprovisioned_manually' ||
        normalizedStatus.includes('migration is in progress') ||
        normalizedStatus.includes('in progress')
    ) {
        return 'warning';
    } else if (normalizedStatus.includes('rejected')) {
        return 'negative';
    } else if (normalizedStatus.includes('withdrawn')) {
        return 'grey';
    }
    return '';
};

/**
 * Extracts cloud name from request object
 * @param {object} request - The request object
 * @returns {string} Lowercase cloud name or 'unknown'
 */
export const getCloudName = (request) => {
    const cloudName = request?.formData?.cloudName || '';
    return cloudName ? cloudName.toLowerCase() : 'unknown';
};

/**
 * Checks if a request is from Multi Tenancy Form
 * @param {object} request - The request object
 * @returns {boolean} True if request is from Multi Tenancy Form
 */
export const isRequestForm3 = (request) => {
    return request?.formData?.requestForm3 === true;
};

/**
 * Gets the batch badge(s) for a request
 * @param {object} request - The request object
 * @returns {string[]} Array of badge texts ('auto' for auto-provisioning, 'MT' for Multi Tenancy Form, empty array otherwise)
 */
export const getBatchBadges = (request) => {
    if (!request?.formData) return [];
    
    const badges = [];
    
    // Check for Multi Tenancy Form
    if (request.formData.requestForm3 === true) {
        badges.push('MT');
    }
    
    // Check for auto provisioning
    if (request.formData.autoProvision === true) {
        badges.push('auto');
    }
    
    return badges;
};

/**
 * Gets a single batch badge text for a request (for backward compatibility)
 * @param {object} request - The request object
 * @returns {string|null} Badge text ('auto' for auto-provisioning, 'MT' for Multi Tenancy Form, null otherwise)
 * @deprecated Use getBatchBadges instead to support multiple badges
 */
export const getBatchBadge = (request) => {
    const badges = getBatchBadges(request);
    return badges.length > 0 ? badges[0] : null;
};

/**
 * Safely gets a nested property from an object
 * @param {object} obj - The object to traverse
 * @param {string} path - Dot-separated path to the property
 * @param {*} defaultValue - Default value if property doesn't exist
 * @returns {*} The property value or default value
 */
export const safeGet = (obj, path, defaultValue = 'N/A') => {
    return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue;
    }, obj);
};

/**
 * Gets submitted by email from request
 * @param {object} request - The request object
 * @returns {string} Submitted by email or 'Unknown'
 */
export const getSubmittedBy = (request) => {
    return safeGet(request, 'formData.submittedBy', 'Unknown');
};

/**
 * Formats timestamp for display
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted timestamp string
 */
export const formatTimestamp = (timestamp) => {
    // Use timezone utility for consistent timezone handling
    // Import dynamically to avoid circular dependencies
    const timezoneUtils = require('./timezone.utils');
    return timezoneUtils.formatTimestamp(timestamp);
};

/**
 * Gets display name for location
 * @param {string} location - The location code
 * @returns {string} Human-readable location name
 */
export const getLocationDisplayName = (location) => {
    switch (location) {
        case 'CentralIndia': return 'Central India';
        case 'EastUS': return 'East US';
        case 'WestUS3': return 'West US3';
        case 'UAENorth': return 'UAE North';
        case 'GermanyWestCentral': return 'Germany West Central';
        default: return 'Unknown Location';
    }
};

/**
 * Highlights search terms in text with a mild background color
 * @param {string} text - The text to highlight
 * @param {string|array} searchTerms - Single search term or array of search terms to highlight
 * @returns {React.ReactNode} JSX with highlighted text
 */
export const highlightSearchTerms = (text, searchTerms) => {
    if (!text || !searchTerms) return text;
    
    // Convert to string and normalize
    const textStr = String(text);
    if (!textStr) return text;
    
    // Normalize search terms to array
    const terms = Array.isArray(searchTerms) 
        ? searchTerms.filter(t => t && String(t).trim())
        : [searchTerms].filter(t => t && String(t).trim());
    
    if (terms.length === 0) return text;
    
    // Create a regex pattern that matches any of the search terms (case-insensitive)
    const pattern = new RegExp(
        '(' + terms.map(term => {
            // Escape special regex characters
            return String(term).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }).join('|') + ')',
        'gi'
    );
    
    // Split text by matches and wrap matches in highlight spans
    const parts = textStr.split(pattern);
    
    return parts.map((part, index) => {
        // Check if this part matches any search term (case-insensitive)
        const isMatch = terms.some(term => 
            part.toLowerCase() === String(term).toLowerCase()
        );
        
        if (isMatch) {
            return (
                <mark
                    key={index}
                    style={{
                        backgroundColor: '#fff3cd',
                        color: '#856404',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontWeight: '500'
                    }}
                >
                    {part}
                </mark>
            );
        }
        return part;
    });
};

